<!-- =========================================================================================

  説明: ダイアログでプロット画像をアップロードするコンポーネント
  作成者: Kokichi

========================================================================================== -->

<template>
  <div>
    <vs-prompt
      @accept="save"
      @cancel="cancel"
      @close="close"
      :active.sync="showDialog"
      :title="title"
      :accept-text="acceptText"
      :cancel-text="cancelText"
      :is-valid="isValid"
    >
      <vs-progress
        v-show="showProgress"
        indeterminate
        color="primary"
      ></vs-progress>

      <div
        v-if="!imgSrc"
        :class="[isDrag ? dragoverClass : dragleaveClass]"
        @dragover.prevent="checkDrag($event, true)"
        @dragleave.prevent="checkDrag($event, false)"
        @drop.prevent="setImage($event)"
        @click="$refs.update_photo_input.click()"
      >
        <input
          type="file"
          ref="update_photo_input"
          class="hidden"
          @change="setImage($event)"
        />
        <span
        >画像ファイルをドロップ、<br/>またはクリックしてファイルを選択</span
        >
      </div>

      <vue-cropper
        v-if="isCrop"
        ref="cropper"
        :src="imgSrc"
        :autoCropArea="0.8"
        :dragMode="'move'"
        :restore="false"
        :guides="false"
        :center="false"
        :highlight="false"
        :background="false"
        :cropBoxMovable="false"
        :cropBoxResizable="false"
        :aspectRatio="1 / 1"
        :toggleDragModeOnDblclick="false"
      ></vue-cropper>
    </vs-prompt>
  </div>
</template>

<script>
  import {createNamespacedHelpers} from "vuex";
  import {storage} from "@/firebase/firebaseConfig";
  import VueCropper from "vue-cropperjs";
  import "cropperjs/dist/cropper.css";
  import {FileUtil} from "@/utils";

  const {mapActions} = createNamespacedHelpers("enterprisePlot");

  export default {
    components: {VueCropper},
    data() {
      return {
        showDialog: false,
        title: "プロット画像の変更",
        acceptText: "保存",
        cancelText: "キャンセル",
        isValid: false,
        showProgress: false,
        pictureData: {},
        pictureUrl: null,
        imgSrc: null,
        fileType: null,
        cropImg: null,
        isDrag: false,
        isCrop: null,
        dragleaveClass: "drag-area-dragleave",
        dragoverClass: "drag-area-dragover"
      };
    },
    methods: {
      ...mapActions(["createPlotPicture", "removePlotPicture"]),
      show() {
        this.showDialog = true;
        this.title = "プロット画像の選択";
      },
      save() {
        this.isCrop = false;
        this.showProgress = true;
        this.title = "プロット画像を登録しています...";
        this.showDialog = true;
        this.isValid = false;
        let base64 = this.$refs.cropper.getCroppedCanvas().toDataURL();
        this.uploadImage(FileUtil.toBlob(base64, this.fileType));
      },
      close() {
        this.cleanUp();
      },
      cancel() {
        this.cleanUp();
      },
      checkDrag(e, isDrag) {
        this.isDrag = isDrag;
      },
      setImage(e) {
        const file =
          typeof e.target.files !== "undefined"
            ? e.target.files[0]
            : e.dataTransfer.files[0];

        if (file.type.indexOf("image/") === -1) {
          return;
        }

        this.fileType = file.type

        let reader = new FileReader();
        reader.onload = e => {
          this.imgSrc = e.target.result;
          this.isValid = true;
          this.isCrop = true;
        };
        reader.readAsDataURL(file);
      },
      async uploadImage(inputFile) {
        let payload = {file: inputFile};
        let res = await this.createPlotPicture(payload);
        await this.checkExistsThumbnail(res);
        this.cleanUp();
        this.$vs.notify({
          title: "写真を追加しました",
          color: "success",
          iconPack: "feather",
          icon: "icon-check",
          position: "top-right"
        });
        this.$emit("pictureUploaded", res);
      },
      checkExistsThumbnail(picture) {
        let errorCount = 0;
        return new Promise((resolve, reject) => {

          setTimeout(() => {
            this.getPictureUrl(picture).then(() => {
              resolve();
            }).catch(() => {
              errorCount++;
            });

            if(errorCount >= 3) {
              reject();
            }
          }, 1000);
        })
      },
      getPictureUrl(picture) {
        return storage
          .child(picture.thumbnail_path)
          .getDownloadURL()
          .then(response => {
            this.pictureUrl = response;
            this.onImageUpdated();
          })
        .catch(_ => {
        })
      },
      onImageUpdated() {
        this.$emit("onImageUpdated", {
          data: this.pictureData,
          url: this.pictureUrl
        });
        this.cleanUp();
      },
      cleanUp() {
        this.showDialog = false;
        this.showProgress = false;
        this.imgSrc = null;
        this.isCrop = false;
        this.isDrag = false;
        this.buttonAccept = true;
      }
    }
  };
</script>

<style lang="scss">
  .drag-area-dragleave {
    width: 100%;
    min-width: 200px;
    height: 100%;
    min-height: 200px;
    border: 2px dotted rgba(0, 0, 0, 0.1);
    border-radius: 8px;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    background-color: #f5f5f5;
    text-align: center;
  }

  .drag-area-dragover {
    width: 100%;
    min-width: 200px;
    height: 100%;
    min-height: 200px;
    border: 2px solid rgba(115, 103, 240, 1);
    border-radius: 8px;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    background-color: #f5f5f5;
    text-align: center;
  }
</style>
